import React, { useState, useEffect } from 'react';
import ExpansionPanel, { ExpansionList } from 'react-md/lib/ExpansionPanels';
import Loading from 'Components/Common/Loading';
import { FN_EMPTY_VOID } from 'Util/Helpers/Empty';
import { TextField } from 'react-md/lib/TextFields';
import { formatCurrencyString } from 'Util/Helpers/Currency';
import './Checkout.scss';
import Cleave from 'cleave.js/react';
import LoadingButton from 'Components/Common/LoadingButton';
import { Link } from 'react-router-dom';
import ValidationModel, {
   ValidationResult,
} from 'Components/Common/ModelValidator/ValidationModel';
import {
   validateModel,
   validateField,
} from 'Components/Common/ModelValidator/Helper';
import { getInputValidationClassName } from 'Util/Helpers/Validation';
import { toast } from 'react-toastify';
import { isEnterKeyPress } from 'Util/Helpers/Input';
import { useParams, useLocation, useHistory } from 'react-router';
import queryString from 'query-string';
import { isNullOrWhiteSpace } from 'Util/Helpers/Validation';
import { useSelector, useDispatch } from 'react-redux';
import {
   selectIsLoading,
   selectIsError,
   selectIsSuccess,
} from 'State/Redux/AsyncRedux';
import {
   selectTransactionAmount,
   selectCardFee,
   PaymentActions,
   PaymentTypes,
   selectPaymentCheckoutResponse,
   selectAmIAllowedToProceed,
} from 'State/Redux/PaymentRedux';

interface CheckoutModel {
   cardNumber: string;
   expiryDate: string;
   cardHolderName: string;
   amount: number;
   cardFee: number;
}

type CheckoutModelReadOnly = Readonly<CheckoutModel>;

const DEFAULT_CHECKOUT_MODEL: CheckoutModelReadOnly = {
   cardNumber: '',
   expiryDate: '',
   cardHolderName: '',
   amount: 0,
   cardFee: 0,
};

export type CheckoutValidationFields = Pick<
   CheckoutModel,
   'cardNumber' | 'expiryDate' | 'cardHolderName'
>;

const CHECKOUT_VALIDATION_MODEL: ValidationModel<CheckoutValidationFields> = {
   errorMessage: 'The model is not valid',
   fields: {
      cardNumber: {
         validation: 'text',
         required: true,
         error: false,
      },
      expiryDate: {
         validation: 'text',
         required: true,
         error: false,
      },
      cardHolderName: {
         validation: 'text',
         required: true,
         error: false,
      },
   },
};

const DEFAULTCHECKOUT_VALIDATION_RESULT: ValidationResult<CheckoutValidationFields> = {
   error: false,
   errorMessage: '',
   fields: {
      cardNumber: {
         errorMessage: '',
         error: false,
      },
      expiryDate: {
         errorMessage: '',
         error: false,
      },
      cardHolderName: {
         errorMessage: '',
         error: false,
      },
   },
};

const Checkout = (): JSX.Element => {
   const history = useHistory();
   const location = useLocation();
   const { referenceNumber } = useParams();

   const getReturnUrl = (qsv: queryString.ParsedQuery<string>): string => {
      return qsv &&
         qsv.returnUrl &&
         !Array.isArray(qsv.returnUrl) &&
         !isNullOrWhiteSpace(qsv.returnUrl)
         ? qsv.returnUrl
         : '';
   };

   const queryStringValues = queryString.parse(location.search);
   const returnUrl = getReturnUrl(queryStringValues);

   const [model, setModel] = useState<CheckoutModelReadOnly>({
      ...DEFAULT_CHECKOUT_MODEL,
   });

   const [validationResult, setValidationResult] = useState<
      ValidationResult<CheckoutValidationFields>
   >({ ...DEFAULTCHECKOUT_VALIDATION_RESULT });

   const [initiated, setInitiated] = useState<boolean>(false);
   const [amountRequestSubmitted, setAmountRequestSubmitted] = useState<
      boolean
   >(false);
   const transactionAmount = useSelector(selectTransactionAmount);
   const cardFeeAmount = useSelector(selectCardFee);
   const amIAllowedToProceed = useSelector(selectAmIAllowedToProceed);

   const isGettingAmountLoading = useSelector(
      selectIsLoading([PaymentTypes.GET_PAYMENT_TRANSACTION_AMOUNT_REQUEST])
   );
   const isGettingAmountError = useSelector(
      selectIsError([PaymentTypes.GET_PAYMENT_TRANSACTION_AMOUNT_REQUEST])
   );
   const isGettingAmountSuccess = useSelector(
      selectIsSuccess([PaymentTypes.GET_PAYMENT_TRANSACTION_AMOUNT_REQUEST])
   );

   const [paymentCheckoutSubmitted, setPaymentCheckoutSubmitted] = useState<
      boolean
   >(false);

   const paymentCheckoutResponse = useSelector(selectPaymentCheckoutResponse);
   const isSubmittingPaymentCheckoutLoading = useSelector(
      selectIsLoading([PaymentTypes.SUBMIT_PAYMENT_CHECKOUT_REQUEST])
   );
   const isSubmittingPaymentCheckoutError = useSelector(
      selectIsError([PaymentTypes.SUBMIT_PAYMENT_CHECKOUT_REQUEST])
   );

   const dispatch = useDispatch();

   useEffect(() => {
      if (!initiated && !amountRequestSubmitted && referenceNumber) {
         setAmountRequestSubmitted(true);
         setInitiated(true);
         dispatch(
            PaymentActions.getPaymentTransactionAmountRequest(referenceNumber)
         );
         dispatch(PaymentActions.getCardFeeRequest(referenceNumber));
         dispatch(PaymentActions.getAmIAllowedRequest());
      }
   }, [initiated, amountRequestSubmitted, dispatch, referenceNumber]);

   useEffect(() => {
      if (
         amountRequestSubmitted &&
         !isGettingAmountError &&
         isGettingAmountSuccess
      ) {
         setModel({ ...model, amount: transactionAmount || 0 });
         setAmountRequestSubmitted(false);
      }
   }, [
      amountRequestSubmitted,
      transactionAmount,
      model,
      isGettingAmountSuccess,
      isGettingAmountError,
   ]);

   const updateModel = (
      newModel: CheckoutModelReadOnly,
      field: keyof CheckoutValidationFields
   ): void => {
      setValidationResult(
         validateField<CheckoutValidationFields>(
            newModel,
            CHECKOUT_VALIDATION_MODEL,
            field,
            validationResult
         )
      );
      setModel(newModel);
   };

   const validateFormFields = (): boolean => {
      const vResult = validateModel<CheckoutValidationFields>(
         model,
         CHECKOUT_VALIDATION_MODEL
      );

      if (!vResult || vResult.error) {
         setValidationResult(vResult);
         toast.error('Validation Failed: Please enter all required fields');
         return false;
      }

      return true;
   };

   const submitPayment = (): void => {
      if (!validateFormFields()) {
         return;
      }

      setPaymentCheckoutSubmitted(true);
      dispatch(
         PaymentActions.submitPaymentCheckoutRequest({
            transactionNumber: referenceNumber || '',
            cardNumber: model.cardNumber,
            cardHolderName: model.cardHolderName,
            expiryDate: model.expiryDate,
         })
      );
   };

   useEffect((): void => {
      if (
         paymentCheckoutSubmitted &&
         !isSubmittingPaymentCheckoutLoading &&
         !isSubmittingPaymentCheckoutError &&
         paymentCheckoutResponse &&
         paymentCheckoutResponse.isSuccess
      ) {
         toast.success('Payment successful.');
         const qsValues = queryString.parse(decodeURIComponent(returnUrl));
         const furtherReturnUrl = getReturnUrl(qsValues);
         history.push(
            `/payment?paymentId=${paymentCheckoutResponse.paymentId}${
               furtherReturnUrl ? `&returnUrl=${furtherReturnUrl}` : ''
            }`
         );
      }

      if (
         paymentCheckoutSubmitted &&
         !isSubmittingPaymentCheckoutLoading &&
         (isSubmittingPaymentCheckoutError ||
            (paymentCheckoutResponse && !paymentCheckoutResponse.isSuccess))
      ) {
         toast.error(
            `Sorry we couldn't process the payment.${
               !isNullOrWhiteSpace(paymentCheckoutResponse.errorMessage)
                  ? ` Error: ${paymentCheckoutResponse.errorMessage}`
                  : ''
            } `
         );
         setPaymentCheckoutSubmitted(false);
      }
   }, [
      paymentCheckoutSubmitted,
      paymentCheckoutResponse,
      isSubmittingPaymentCheckoutLoading,
      isSubmittingPaymentCheckoutError,
      history,
      returnUrl,
   ]);

   return (
      <Loading
         tryReload={FN_EMPTY_VOID}
         isLoading={
            isGettingAmountLoading || isSubmittingPaymentCheckoutLoading
         }
         isError={false}
         overlayOnChildren
      >
         <ExpansionList className="md-cell md-cell--6 center payment-checkout-container">
            {!amIAllowedToProceed && (
               <ExpansionPanel
                  label="Unable to proceed"
                  className="md-expansion-panel-error"
                  footer={null}
                  expanded
                  expanderIcon={<></>}
                  onExpandToggle={FN_EMPTY_VOID}
               >
                  <div className="md-grid md-cell--12">
                     <div className="md-cell md-cell--12 error-text">
                        There seems to have an issue with your account. <br />
                        We couldn&apos;t find the credential for your account in
                        order to proceed with payment.
                        <br />
                        <br />
                        Please contact Administrator for help.
                     </div>
                  </div>
               </ExpansionPanel>
            )}
            <ExpansionPanel
               label="Payment Checkout"
               footer={null}
               expanded
               expanderIcon={<></>}
               onExpandToggle={FN_EMPTY_VOID}
            >
               <div className="md-grid md-cell--12">
                  <div className="md-cell md-cell--4 md-cell--2-offset">
                     <TextField
                        id="textfield-transactionNumber"
                        label="Reference Number"
                        type="text"
                        value={referenceNumber}
                        disabled
                     />
                  </div>
                  <div className="md-cell md-cell--4">
                     <TextField
                        id="textfield-amount"
                        label="Amount"
                        type="text"
                        className="total-amount"
                        value={formatCurrencyString(model.amount)}
                        disabled
                     />
                  </div>
                  <div className="md-cell md-cell--12">
                     <br />
                     <h3>Credit Card Detail</h3>
                  </div>
                  <div className="md-cell md-cell--4 md-cell--2-offset">
                     <div className="md-text-field-container md-full-width md-text-field-container--input">
                        <label
                           htmlFor="textfield-cardnumber"
                           className={`md-floating-label md-floating-label--floating ${
                              validationResult &&
                              validationResult.fields.cardNumber.error
                                 ? 'md-text--error'
                                 : ''
                           }`}
                        >
                           Card Number *
                        </label>
                        <Cleave
                           placeholder="Enter card number"
                           options={{ creditCard: true }}
                           name="textfield-cardnumber"
                           className={`md-text-field md-text-field--floating-margin md-full-width ${
                              validationResult &&
                              validationResult.fields.cardNumber.error
                                 ? 'input-error'
                                 : ''
                           }`}
                           value={model.cardNumber}
                           onChange={(e): void => {
                              updateModel(
                                 {
                                    ...model,
                                    cardNumber: e.target.rawValue,
                                 },
                                 'cardNumber'
                              );
                           }}
                           onBlur={(e): void => {
                              updateModel(
                                 {
                                    ...model,
                                 },
                                 'cardNumber'
                              );
                           }}
                           disabled={!amIAllowedToProceed}
                        />
                        <hr
                           className={`md-divider md-divider--text-field md-divider--expand-from-left ${
                              validationResult &&
                              validationResult.fields.cardNumber.error
                                 ? 'md-divider--text-field-error'
                                 : ''
                           }`}
                        ></hr>
                        {validationResult &&
                        validationResult.fields.cardNumber.error ? (
                           <div className="md-text-field-message-container md-full-width md-text--error">
                              <div
                                 aria-hidden="false"
                                 className="md-text-field-message md-text-field-message--active"
                              >
                                 Please enter the card number
                              </div>
                           </div>
                        ) : (
                           <div className="md-text-field-message-container md-text-field-message-container--count-only md-full-width md-text--disabled">
                              <div
                                 aria-hidden="true"
                                 className="md-text-field-message md-text-field-message--inactive"
                              >
                                 Please enter the card number
                              </div>
                           </div>
                        )}
                     </div>
                  </div>
                  <div className="md-cell md-cell--4">
                     <div className="md-text-field-container md-full-width md-text-field-container--input">
                        <label
                           htmlFor="textfield-expirydate"
                           className={`md-floating-label md-floating-label--floating ${
                              validationResult &&
                              validationResult.fields.expiryDate.error
                                 ? 'md-text--error'
                                 : ''
                           }`}
                        >
                           Expiry Date *
                        </label>
                        <Cleave
                           placeholder="MM/YY"
                           options={{ date: true, datePattern: ['m', 'y'] }}
                           name="textfield-expirydate"
                           className={`md-text-field md-text-field--floating-margin md-full-width ${
                              validationResult &&
                              validationResult.fields.expiryDate.error
                                 ? 'input-error'
                                 : ''
                           }`}
                           value={model.expiryDate}
                           onChange={(e): void => {
                              updateModel(
                                 {
                                    ...model,
                                    expiryDate: e.target.rawValue,
                                 },
                                 'expiryDate'
                              );
                           }}
                           onBlur={(e): void => {
                              updateModel(
                                 {
                                    ...model,
                                 },
                                 'expiryDate'
                              );
                           }}
                           disabled={!amIAllowedToProceed}
                        />
                        <hr
                           className={`md-divider md-divider--text-field md-divider--expand-from-left ${
                              validationResult &&
                              validationResult.fields.expiryDate.error
                                 ? 'md-divider--text-field-error'
                                 : ''
                           }`}
                        ></hr>
                        {validationResult &&
                        validationResult.fields.expiryDate.error ? (
                           <div className="md-text-field-message-container md-full-width md-text--error">
                              <div
                                 aria-hidden="false"
                                 className="md-text-field-message md-text-field-message--active"
                              >
                                 Please enter the expiry date
                              </div>
                           </div>
                        ) : (
                           <div className="md-text-field-message-container md-text-field-message-container--count-only md-full-width md-text--disabled">
                              <div
                                 aria-hidden="true"
                                 className="md-text-field-message md-text-field-message--inactive"
                              >
                                 Please enter the expiry date
                              </div>
                           </div>
                        )}
                     </div>
                  </div>
                  <div className="md-cell md-cell--8 md-cell--2-offset">
                     <TextField
                        floating
                        id="textfield-cardHolderName"
                        label="Name On Card"
                        type="text"
                        placeholder="Enter the name on the card"
                        required
                        value={model.cardHolderName}
                        onChange={(val): void => {
                           updateModel(
                              {
                                 ...model,
                                 cardHolderName: val.toString(),
                              },
                              'cardHolderName'
                           );
                        }}
                        inputClassName={getInputValidationClassName(
                           validationResult &&
                              validationResult.fields.cardHolderName.error
                        )}
                        error={
                           validationResult &&
                           validationResult.fields.cardHolderName.error
                        }
                        errorText="Please enter the name on the card"
                        disabled={!amIAllowedToProceed}
                     />
                  </div>
                  <div className="md-cell md-cell--8 md-cell--2-offset">
                     <div>
                        Please note a convenience fee of ${cardFeeAmount} will
                        be charged for this payment.
                     </div>
                     <br />
                     <LoadingButton
                        isLoading={isSubmittingPaymentCheckoutLoading}
                        flat
                        primary
                        swapTheming
                        onClick={(): void => submitPayment()}
                        onKeyUp={(keyPress): void => {
                           if (isEnterKeyPress(keyPress)) submitPayment();
                        }}
                        disabled={model.amount === 0 || !amIAllowedToProceed}
                     >
                        Submit Payment
                     </LoadingButton>
                  </div>
                  <div className="md-cell md-cell--8 md-cell--2-offset">
                     <Link to={decodeURIComponent(returnUrl)}>Cancel</Link>
                  </div>
                  <div className="md-cell md-cell--8 md-cell--2-offset">
                     <br />
                     <br />
                     <img
                        className="windcave-logo"
                        id="DpsFooterLogoDps"
                        src="data:image/svg+xml;base64,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"
                        alt="Windcave Logo"
                        title="Windcave Logo"
                     ></img>
                     <br />
                     <a
                        href="https://sec.windcave.com/pxmi3/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                     >
                        Privacy Policy
                     </a>
                  </div>
               </div>
            </ExpansionPanel>
         </ExpansionList>
      </Loading>
   );
};
export default Checkout;
